// Each pane's tab bar (cmux's): the pane's tabs, one session each, the one it shows joined to the pane below with a
// blue top edge (grey in a pane that isn't focused), then + (a new session as a tab here), and the pane's tools:
// Voice, Split right, Split down, Close pane. Drag a tab to move it along its bar (Alt+Shift+arrow from the
// keyboard), or out of it onto another pane: its bar (where it goes in), its middle (at the end), or an edge of any
// pane (a new pane there, the tab moved into it: cmux's). It never starts a session (that's + and Split's). Over all of them sits the workspace's bar (TopBar, cmux's title bar): the row's folder and name, or the
// page's, and the machine.

import { IconArrowsDiagonalMinimize, IconDeviceDesktop, IconFolder, IconKeyboard, IconLayoutColumns, IconLayoutRows, IconLayoutSidebarLeftExpand, IconMicrophone, IconPlus, IconServer, IconSettings, IconX } from '@tabler/icons-react';
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { Session } from '../net/protocol';
import { useStore } from '../store';
import { Ic, Mark, type MarkTone, Tool, Tooltip, presence, usePresence } from './ds';
import { useDriven } from './driven';
import { MachineChip } from './Machine';
import { isMac } from './keys';
import { RenameDialog } from './RenameDialog';
import { current, isBlank, MAX_PANES, type Page, type Pane, type Side } from './tabs';
import { useVoiceKeyLabel, VoiceButton } from './VoiceCard';
import { useActions } from './Workspace';

export const STATUS_TONE: Record<Session['status'], MarkTone> = { working: 'run', waiting: 'attn', done: 'live', idle: 'idle', exited: 'fault' };

/** A session's status mark, the same everywhere (spec "Status marks"): working blinks (blue), what needs you is steady
 *  with its halo (amber), done is lit (green), idle and exited are still. */
export const statusMark = (st: Session['status']) => ({ tone: STATUS_TONE[st], ring: st === 'waiting', blink: st === 'working' });

const base = (p?: string) => (p ? p.replace(/\/+$/, '').split('/').pop() || p : '');

/** A session's tab name: "claude · shop" for an agent, else its own name. */
export function sessionTabName(s: Session) {
  const m = s.row?.meta.none;
  return m?.program && m.where ? `${m.program} · ${base(m.where)}` : s.name;
}

/** The little filled terminal tile a session tab leads with. */
const TermTile = () => (
  <svg className="ds-ic" width="14" height="12" viewBox="0 0 14 12" aria-hidden="true">
    <rect width="14" height="12" rx="3" fill="currentColor" />
    <svg x="2" y="1" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--surface)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 7l5 5l-5 5" />
      <path d="M12 19l7 0" />
    </svg>
  </svg>
);

/** Each page's name and icon: Desktop the monitor, Files the folder, Settings the gear, the machine the server. */
export function usePageLabel() {
  const machine = useStore((s) => s.me?.machine?.id) || 'Machine';
  return (p: Page): { name: string; icon: typeof IconServer } =>
    p === 'desktop' ? { name: 'Desktop', icon: IconDeviceDesktop } : p === 'files' ? { name: 'Files', icon: IconFolder } : p === 'settings' ? { name: 'Settings', icon: IconSettings } : { name: machine, icon: IconServer };
}

type Drag = {
  key: string;
  at: number;
  from: number;
  pointer: number;
  startX: number;
  startY: number;
  grab: number;
  grabY: number;
  x: number;
  lifted: boolean;
  /** Out of its bar: a ghost follows the pointer, and another pane (its bar, or its body) is where it goes. */
  away: boolean;
  target: { pane: number; at: number } | { pane: number; side: Side } | null;
};

/** The workspace's panes in sight, each with its bar's and its own box (none on a phone: one pane, no bars; one while
 *  it's zoomed). */
const paneBoxes = () =>
  [...document.querySelectorAll<HTMLElement>('.ws-panes > .ws-cell:not([data-hidden]) [data-testid=pane][data-pane]')].map((el) => ({
    pane: Number(el.dataset.pane),
    el,
    box: el.getBoundingClientRect(),
    bar: el.querySelector<HTMLElement>('.ws-panebar')?.getBoundingClientRect() ?? null,
  }));
const inside = (r: DOMRect, x: number, y: number) => x >= r.left && x < r.right && y >= r.top && y < r.bottom;

/** How far in from a pane's edge dropping splits it there (a share of its width or height); the middle joins it. */
const SPLIT_BAND = 0.3;

/** The edge band of `r` that (x, y) is in, nearest edge first at the corners; null in the middle. */
function sideAt(r: DOMRect, x: number, y: number): Side | null {
  const d: [Side, number][] = [
    ['left', (x - r.left) / r.width],
    ['right', (r.right - x) / r.width],
    ['top', (y - r.top) / r.height],
    ['bottom', (r.bottom - y) / r.height],
  ];
  const [side, k] = d.reduce((a, b) => (b[1] < a[1] ? b : a));
  return k < SPLIT_BAND ? side : null;
}

/** The marks a drag out of the bar puts on the page: the ghost, the line where it goes in, the pane it'd join. */
function dragMarks() {
  const make = (cls: string) => {
    const el = document.createElement('div');
    el.className = cls;
    el.setAttribute('aria-hidden', 'true');
    el.hidden = true;
    document.body.append(el);
    return el;
  };
  return { ghost: make('ws-tab-ghost'), line: make('ws-drop-line'), pane: make('ws-drop-pane') };
}

/**
 * Dragging a tab along the strip, as the Mac's: past 4px it lifts and follows the pointer, and when its middle crosses
 * a neighbour's middle the two swap; the others glide into the room it leaves (each slides from where it was) and it
 * settles into its slot when let go. Taken out of its bar it's a ghost under the pointer (cmux's) and the tab waits
 * in place, faded: over another pane's bar a line shows where it goes in, over another pane's middle the pane lights
 * (it goes at the end), and over the outer 30% of any pane (its own too, when it has another tab to keep; not at 4
 * panes) that half lights: let go there and the pane splits that way with the tab in the new half. Anywhere else it
 * stays. Never a session. Esc puts it back where it started. Mouse and pen only: a finger scrolls the strip.
 */
function useTabDrag(
  index: number,
  moveTab: (from: number, to: number) => void,
  showTab: (k: number) => void,
  moveTo: (k: number, pane: number, at: number) => void,
  moveToSplit: (k: number, pane: number, side: Side) => void,
) {
  const list = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  /** Every tab's left before a move, so each can slide from there to its new place. */
  const before = useRef<Map<string, number> | null>(null);
  const tabs = () => [...(list.current?.children ?? [])].filter((e): e is HTMLElement => e instanceof HTMLElement && e.classList.contains('ws-tab'));
  const lifted = () => tabs().find((e) => e.dataset.key === drag.current?.key);
  const marks = useRef<ReturnType<typeof dragMarks> | null>(null);
  const place = () => {
    const el = lifted();
    if (el && drag.current && !drag.current.away) el.style.transform = `translateX(${drag.current.x - el.offsetLeft}px)`;
  };
  /** Out of the bar: the tab waits in its slot, faded, and a ghost of it follows the pointer. */
  const goAway = (e: globalThis.PointerEvent) => {
    const d = drag.current;
    const el = lifted();
    if (!d || !el) return;
    d.away = true;
    delete el.dataset.dragging;
    el.style.transform = '';
    el.dataset.away = '';
    marks.current ??= dragMarks();
    const g = marks.current.ghost;
    g.replaceChildren(...[...(el.querySelector('.ws-tab-main')?.childNodes ?? [])].map((n) => n.cloneNode(true)));
    g.hidden = false;
    follow(e);
  };
  /** Back over its own bar: the tab is held again, as along the strip. */
  const comeBack = () => {
    const d = drag.current;
    const el = lifted();
    if (!d) return;
    d.away = false;
    d.target = null;
    if (el) {
      delete el.dataset.away;
      el.dataset.dragging = '';
    }
    if (marks.current) for (const m of Object.values(marks.current)) m.hidden = true;
  };
  /** The ghost under the pointer, and what it's over: another pane's bar (the gap nearest), its body, or nothing. */
  const follow = (e: globalThis.PointerEvent) => {
    const d = drag.current;
    const m = marks.current;
    if (!d || !m) return;
    m.ghost.style.transform = `translate(${e.clientX - d.grab}px, ${e.clientY - d.grabY}px)`;
    d.target = null;
    m.line.hidden = true;
    m.pane.hidden = true;
    const boxes = paneBoxes();
    const over = boxes.find((p) => inside(p.box, e.clientX, e.clientY));
    if (!over) return;
    const own = over.pane === index;
    if (!own && over.bar && inside(over.bar, e.clientX, e.clientY)) {
      const strip = over.el.querySelector<HTMLElement>('.ws-tabs');
      const els = [...(strip?.querySelectorAll<HTMLElement>(':scope > .ws-tab') ?? [])];
      const at = els.filter((t) => {
        const r = t.getBoundingClientRect();
        return r.left + r.width / 2 < e.clientX;
      }).length;
      const sr = strip?.getBoundingClientRect() ?? over.bar;
      const edge = at < els.length ? els[at].getBoundingClientRect().left : els.length ? els[els.length - 1].getBoundingClientRect().right : sr.left;
      const x = Math.min(Math.max(edge, sr.left + 1), sr.right - 1);
      m.line.style.transform = `translate(${x - 1}px, ${over.bar.top + over.bar.height / 2 - 8}px)`;
      m.line.hidden = false;
      d.target = { pane: over.pane, at };
      return;
    }
    // an edge splits (room for a pane, and in its own pane another tab to keep); the middle joins another pane
    const r = over.box;
    const canSplit = boxes.length < MAX_PANES && (!own || tabs().length > 1);
    const side = canSplit ? sideAt(r, e.clientX, e.clientY) : null;
    if (!side && own) return;
    const half = !side
      ? { left: r.left, top: r.top, width: r.width, height: r.height }
      : side === 'left' || side === 'right'
        ? { left: side === 'left' ? r.left : r.left + r.width / 2, top: r.top, width: r.width / 2, height: r.height }
        : { left: r.left, top: side === 'top' ? r.top : r.top + r.height / 2, width: r.width, height: r.height / 2 };
    Object.assign(m.pane.style, { left: `${half.left}px`, top: `${half.top}px`, width: `${half.width}px`, height: `${half.height}px` });
    m.pane.dataset.side = side ?? '';
    m.pane.hidden = false;
    d.target = side ? { pane: over.pane, side } : { pane: over.pane, at: Number.MAX_SAFE_INTEGER };
  };
  const move = (from: number, to: number) => {
    before.current = new Map(tabs().map((e) => [e.dataset.key ?? '', e.offsetLeft]));
    if (drag.current) drag.current.at = to;
    moveTab(from, to);
  };
  const end = () => {
    const el = lifted();
    if (el) {
      delete el.dataset.dragging;
      delete el.dataset.away;
      el.style.transform = '';
    }
    delete document.documentElement.dataset.tabDrag;
    const m = marks.current;
    marks.current = null;
    if (m) {
      m.line.remove();
      m.pane.remove();
      // the ghost fades where it is (150ms), then goes
      if (m.ghost.hidden) m.ghost.remove();
      else {
        m.ghost.dataset.leaving = '';
        setTimeout(() => m.ghost.remove(), 160);
      }
    }
    drag.current = null;
    window.removeEventListener('pointermove', win.current.move);
    window.removeEventListener('pointerup', win.current.up);
    window.removeEventListener('pointercancel', win.current.cancel);
    window.removeEventListener('keydown', win.current.key, true);
  };
  const onMove = (e: globalThis.PointerEvent) => {
    const d = drag.current;
    const strip = list.current;
    if (!d || !strip || e.pointerId !== d.pointer) return;
    if (!d.lifted) {
      if (Math.abs(e.clientX - d.startX) < 4 && Math.abs(e.clientY - d.startY) < 4) return;
      d.lifted = true;
      const el = lifted();
      if (el) el.dataset.dragging = '';
      document.documentElement.dataset.tabDrag = '';
      window.addEventListener('keydown', win.current.key, true);
      showTab(d.at);
    }
    e.preventDefault();
    // out of its bar (a little slack above and below), it's a ghost looking for another pane; back over it, along the strip
    const bar = strip.closest('.ws-panebar')?.getBoundingClientRect();
    const onBar = !!bar && e.clientX >= bar.left && e.clientX < bar.right && e.clientY >= bar.top - 8 && e.clientY < bar.bottom + 8;
    if (!onBar) {
      if (!d.away) goAway(e);
      else follow(e);
      return;
    }
    if (d.away) comeBack();
    d.x = e.clientX - d.grab - strip.getBoundingClientRect().left + strip.scrollLeft;
    place();
    const els = tabs();
    const el = els[d.at];
    if (!el) return;
    // a neighbour gives way once the dragged tab covers half of it (its leading edge over the neighbour's middle), as
    // Chrome's tabs do; after the swap the same test points the other way, so it never undoes itself
    const next = els[d.at + 1];
    const prev = els[d.at - 1];
    if (next && d.x + el.offsetWidth > next.offsetLeft + next.offsetWidth / 2) move(d.at, d.at + 1);
    else if (prev && d.x < prev.offsetLeft + prev.offsetWidth / 2) move(d.at, d.at - 1);
  };
  const onUp = (e: globalThis.PointerEvent) => {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    if (d.lifted) {
      // letting go after a drag isn't a click on the tab under the pointer
      const stop = (c: MouseEvent) => {
        c.stopPropagation();
        c.preventDefault();
      };
      window.addEventListener('click', stop, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', stop, true), 0);
    }
    const target = d.away ? d.target : null;
    const back = d.away && !target && d.at !== d.from;
    const k = d.at;
    if (back) move(d.at, d.from); // let go over nothing: it stays where it was
    end();
    if (target && 'side' in target) moveToSplit(k, target.pane, target.side);
    else if (target) moveTo(k, target.pane, target.at);
  };
  const onKey = (e: globalThis.KeyboardEvent) => {
    const d = drag.current;
    if (e.key !== 'Escape' || !d) return;
    e.preventDefault();
    e.stopPropagation();
    move(d.at, d.from);
    end();
  };
  // The drag follows the pointer on the window, not on the tab: React moves the tab's element when the order
  // changes, and an element taken out of the page loses its pointer capture, so the drag stopped at the first swap.
  // Stable listeners (added on press, removed at the end) reaching this render's handlers.
  const latest = useRef({ onMove, onUp, onKey, end });
  latest.current = { onMove, onUp, onKey, end };
  const win = useRef({
    move: (e: globalThis.PointerEvent) => latest.current.onMove(e),
    up: (e: globalThis.PointerEvent) => latest.current.onUp(e),
    cancel: () => latest.current.end(),
    key: (e: globalThis.KeyboardEvent) => latest.current.onKey(e),
  });

  // after a move: the others slide from their old place (none with reduced motion: the CSS has no transition), and
  // the lifted one stays under the pointer in its new slot
  useLayoutEffect(() => {
    const was = before.current;
    before.current = null;
    if (was) {
      for (const el of tabs()) {
        if (el.dataset.key === drag.current?.key) continue;
        const dx = (was.get(el.dataset.key ?? '') ?? el.offsetLeft) - el.offsetLeft;
        if (!dx) continue;
        el.style.transition = 'none';
        el.style.transform = `translateX(${dx}px)`;
        void el.offsetWidth;
        el.style.transition = '';
        el.style.transform = '';
      }
    }
    const el = lifted();
    if (drag.current?.lifted && el) {
      // a re-render can bring a new element for it
      if (drag.current.away) el.dataset.away = '';
      else {
        el.dataset.dragging = '';
        place();
      }
    }
  });

  const handlers = (k: number, key: string) => ({
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || e.pointerType === 'touch' || (e.target as Element).closest('.ws-tab-close')) return;
      if (drag.current) end();
      const r = e.currentTarget.getBoundingClientRect();
      drag.current = { key, at: k, from: k, pointer: e.pointerId, startX: e.clientX, startY: e.clientY, grab: e.clientX - r.left, grabY: e.clientY - r.top, x: 0, lifted: false, away: false, target: null };
      window.addEventListener('pointermove', win.current.move);
      window.addEventListener('pointerup', win.current.up);
      window.addEventListener('pointercancel', win.current.cancel);
    },
  });

  return { list, handlers };
}

/** While the sidebar is hidden: the wordmark and Show sidebar, at the left of the top-left bar. */
function Lead() {
  const a = useActions();
  return (
    <div className="ws-strip-lead">
      <span className="ws-wordmark">cmx_</span>
      <Tool icon={IconLayoutSidebarLeftExpand} label="Show sidebar" k={isMac() ? 'B' : undefined} onClick={a.toggleSidebar} />
    </div>
  );
}

/** Who is typing into the session a pane shows through the API (driven.ts: an agent driving it with `cmx tmux`): a
 *  quiet chip in its bar that fades in, stays while input keeps coming and fades out 3 s after the last. */
function DrivenPill({ sid }: { sid: number | undefined }) {
  const who = useDriven(sid);
  const last = useRef(who);
  if (who) last.current = who;
  const p = usePresence(who !== null, 150);
  if (!p.mounted || !last.current) return null;
  return (
    <span className="ws-driven" {...presence(p)} data-testid="driven" title={`${last.current} is typing here`}>
      <Ic icon={IconKeyboard} size={12} />
      <span className="ws-driven-who">{last.current}</span>
    </span>
  );
}

/** A pane's tab bar: its tabs, + and its tools. */
export function PaneBar({ index, pane, focused, multi }: { index: number; pane: Pane; focused: boolean; multi: boolean }) {
  const a = useActions();
  const voiceKey = useVoiceKeyLabel();
  const sessions = useStore((s) => s.sessions);
  const { list, handlers } = useTabDrag(
    index,
    (from, to) => a.moveTab(index, from, to),
    (k) => a.showTab(index, k),
    (k, to, at) => a.moveTabTo(index, k, to, at),
    (k, to, side) => a.moveTabToSplit(index, k, to, side),
  );
  /** Said to a screen reader after a tab moves from the keyboard. */
  const [moved, setMoved] = useState('');
  const count = a.paneCount;
  const full = count >= MAX_PANES;
  const splitSub = full ? '4 panes open' : 'up to 4';
  const shown = pane.tabs[pane.active];
  const shownSession = sessions.find((x) => x.id === shown);
  // this pane fills the workspace (the machine's zoom: tmux's resize-pane -Z); clicking shows every pane again
  const zoomed = focused && multi && !!current(a.tabs)?.zoomed;

  return (
    <div className="ws-strip ws-panebar" data-testid="pane-bar" data-focused={focused ? '' : undefined}>
      <div className="ws-tabs" role="tablist" aria-label={multi ? `Pane ${index + 1} tabs` : 'Tabs'} ref={list}>
        {pane.tabs.map((id, k) => {
          // a blank tab (+): "New tab", no status mark, not dragged
          const blank = isBlank(id);
          const s = sessions.find((x) => x.id === id);
          const name = blank ? 'New tab' : s ? sessionTabName(s) : 'Session';
          const on = k === pane.active;
          const key = `s${id}`;
          const keyMove = (e: KeyboardEvent<HTMLButtonElement>) => {
            if (!e.altKey || !e.shiftKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
            e.preventDefault();
            const to = k + (e.key === 'ArrowLeft' ? -1 : 1);
            if (to < 0 || to >= pane.tabs.length) return;
            a.moveTab(index, k, to);
            setMoved(`${name}, tab ${to + 1} of ${pane.tabs.length}`);
          };
          return (
            <div
              key={key}
              data-key={key}
              {...(blank ? {} : handlers(k, key))}
              className="ws-tab"
              data-state={on ? (focused || !multi ? 'current' : 'shown') : undefined}
              data-testid="tab"
              data-tab={key}
            >
              <button
                type="button"
                role="tab"
                aria-selected={on}
                className="ws-tab-main"
                onClick={() => a.showTab(index, k)}
                onKeyDown={keyMove}
                onAuxClick={(e) => {
                  if (e.button === 1) (e.preventDefault(), a.closeTab(index, k));
                }}
              >
                <TermTile />
                {!blank && <Mark {...statusMark(s?.status ?? 'idle')} />}
                <span className="ws-tab-name" data-name={name}>
                  {name}
                </span>
              </button>
              {/* every tab keeps the close button's room, so picking one doesn't move the others: it shows on the
                  tab shown and on hover */}
              <button
                type="button"
                className="ws-tab-close"
                aria-label={`Close ${name}`}
                tabIndex={on ? 0 : -1}
                data-testid={on ? 'tab-close' : undefined}
                onClick={() => a.closeTab(index, k)}
              >
                <Ic icon={IconX} size={12} />
              </button>
            </div>
          );
        })}
      </div>
      <span className="ds-sr" aria-live="polite">
        {moved}
      </span>
      <span className="ws-strip-tail">
        {/* 24 in the 36 bar: 6 clear of the top, as the tabs' labels sit */}
        <Tool icon={IconPlus} iconSize={14} size="xs" radius={6} label="New tab" onClick={() => a.addBlank(index)} data-testid="new-tab" />
        <span className="ws-grow" />
        <DrivenPill sid={shownSession?.id} />
        {zoomed && <Tool icon={IconArrowsDiagonalMinimize} iconSize={14} size="xs" radius={6} label="Zoomed" sub="click to show every pane" align="end" pressed onClick={a.unzoom} data-testid="pane-zoomed" />}
        {shownSession && shownSession.status !== 'exited' && (
          <Tooltip label="Voice" sub={`or hold ${voiceKey}`} align="end">
            <VoiceButton sid={shownSession.id} className="ds-btn" testid="voice-button" extra={{ 'data-variant': 'icon', 'data-size': 'xs', 'data-radius': '6' }}>
              <Ic icon={IconMicrophone} size={14} />
            </VoiceButton>
          </Tooltip>
        )}
        <Tool icon={IconLayoutColumns} iconSize={14} size="xs" radius={6} label="Split right" sub={splitSub} align="end" disabled={full} onClick={() => a.split('row', index)} data-testid="pane-split-right" />
        <Tool icon={IconLayoutRows} iconSize={14} size="xs" radius={6} label="Split down" sub={splitSub} align="end" disabled={full} onClick={() => a.split('col', index)} data-testid="pane-split-down" />
        {multi && <Tool icon={IconX} iconSize={14} size="xs" radius={6} label="Close pane" sub="its sessions go on" align="end" onClick={() => a.closePane(index)} data-testid="close-pane" />}
      </span>
    </div>
  );
}

/**
 * The workspace's bar (cmux's title bar), over every pane: the row it shows, its folder and name (double-click renames
 * it, as the row's Rename does: one name, both places), or the page in its place with that page's icon and name; then
 * the machine. While the sidebar is hidden it leads with the wordmark and Show sidebar. Rows and pages alike get it, so
 * the top of the window never jumps.
 */
export function TopBar({ page, lead }: { page: Page | null; lead?: number }) {
  const a = useActions();
  const pageLabel = usePageLabel();
  const s = useStore((st) => (lead === undefined ? undefined : st.sessions.find((x) => x.id === lead)));
  const [editing, setEditing] = useState(false);
  const label = page ? pageLabel(page) : s ? { name: s.name, icon: IconFolder } : null;
  const renames = !page && s;
  return (
    <header className="ws-strip ws-topbar" data-testid="top-bar" data-page={page ?? undefined}>
      {a.sidebarCollapsed && <Lead />}
      {label && (
        <h1 className="ws-topbar-title" data-testid="top-title" title={renames ? 'Double-click to rename' : undefined} onDoubleClick={renames ? () => setEditing(true) : undefined}>
          <Ic icon={label.icon} size={16} />
          <span className="ws-topbar-name">{label.name}</span>
        </h1>
      )}
      <span className="ws-grow" />
      <MachineChip />
      {s && <RenameDialog open={editing} name={s.name} automatic={s.nameSource === 'user'} onSave={(name) => void a.rename(s.id, name)} onClose={() => setEditing(false)} />}
    </header>
  );
}
